import React from 'react';
import { Shield, Star, Award, Clock, Verified, Trophy, ThumbsUp, Timer, Medal } from 'lucide-react';

/**
 * Driver badge definitions with icons and colors
 */
const BADGE_CONFIG = {
  verified: {
    icon: Shield,
    label: 'Verified',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    description: 'Background check completed'
  },
  top_rated: {
    icon: Star,
    label: 'Top Rated',
    color: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    description: '4.8+ average rating'
  },
  veteran: {
    icon: Trophy,
    label: 'Veteran',
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    description: '100+ completed rides'
  },
  punctual: {
    icon: Clock,
    label: 'Punctual',
    color: 'bg-green-100 text-green-700 border-green-200',
    description: '95%+ on-time pickups'
  },
  preferred: {
    icon: ThumbsUp,
    label: 'Preferred',
    color: 'bg-orange-100 text-orange-700 border-orange-200',
    description: 'Customer favorite'
  },
  five_star: {
    icon: Medal,
    label: '5-Star Driver',
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    description: 'Perfect 5.0 rating'
  },
  fast_responder: {
    icon: Timer,
    label: 'Fast Responder',
    color: 'bg-cyan-100 text-cyan-700 border-cyan-200',
    description: 'Quick acceptance rate'
  }
};

/**
 * DriverBadges component - displays driver achievement badges with icons
 * @param {string[]} badges - Array of badge IDs
 * @param {string} size - 'sm' | 'md' | 'lg'
 * @param {boolean} showLabels - Whether to show badge labels
 * @param {number} maxDisplay - Maximum badges to display before "+X more"
 */
const DriverBadges = ({ 
  badges = [], 
  size = 'sm', 
  showLabels = true, 
  maxDisplay = 5,
  className = ''
}) => {
  if (!badges || badges.length === 0) return null;

  // Size configurations
  const sizeClasses = {
    sm: { icon: 'w-3.5 h-3.5', pill: 'px-2 py-0.5 text-xs gap-1', iconOnly: 'w-6 h-6' },
    md: { icon: 'w-4 h-4', pill: 'px-2.5 py-1 text-sm gap-1.5', iconOnly: 'w-8 h-8' },
    lg: { icon: 'w-5 h-5', pill: 'px-3 py-1.5 text-sm gap-2', iconOnly: 'w-10 h-10' }
  };

  const sizes = sizeClasses[size] || sizeClasses.sm;
  const displayBadges = badges.slice(0, maxDisplay);
  const remainingCount = badges.length - maxDisplay;

  const getBadgeConfig = (badge) => {
    // Normalize badge name to lowercase with underscores
    const normalizedBadge = badge.toLowerCase().replace(/\s+/g, '_');
    return BADGE_CONFIG[normalizedBadge] || {
      icon: Award,
      label: badge.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      color: 'bg-gray-100 text-gray-700 border-gray-200',
      description: badge
    };
  };

  return (
    <div className={`flex flex-wrap gap-1.5 ${className}`} data-testid="driver-badges">
      {displayBadges.map((badge, idx) => {
        const config = getBadgeConfig(badge);
        const Icon = config.icon;

        return (
          <div
            key={`${badge}-${idx}`}
            className={`inline-flex items-center rounded-full border ${config.color} ${showLabels ? sizes.pill : sizes.iconOnly + ' justify-center'}`}
            title={config.description}
            data-testid={`badge-${badge.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <Icon className={sizes.icon} />
            {showLabels && (
              <span className="font-medium">{config.label}</span>
            )}
          </div>
        );
      })}
      
      {remainingCount > 0 && (
        <span 
          className={`inline-flex items-center rounded-full border border-gray-200 bg-gray-50 text-gray-600 ${showLabels ? sizes.pill : sizes.iconOnly + ' justify-center'}`}
          title={`${remainingCount} more badges`}
        >
          +{remainingCount}
        </span>
      )}
    </div>
  );
};

/**
 * DriverBadgeIcon - Single badge icon for compact display
 */
export const DriverBadgeIcon = ({ badge, size = 'sm', showTooltip = true }) => {
  const normalizedBadge = badge.toLowerCase().replace(/\s+/g, '_');
  const config = BADGE_CONFIG[normalizedBadge] || {
    icon: Award,
    label: badge,
    color: 'text-gray-500',
    description: badge
  };
  const Icon = config.icon;

  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  return (
    <span 
      className={`inline-flex ${config.color.split(' ')[1]}`}
      title={showTooltip ? `${config.label}: ${config.description}` : undefined}
    >
      <Icon className={sizeClasses[size]} />
    </span>
  );
};

export default DriverBadges;
